import { z } from 'zod';
import { AuditSchema, IdSchema, InstantSchema } from './common.ts';

/**
 * A notice everybody has to read and acknowledge.
 *
 * Asked for as a splash message carrying OSHA health-and-safety reminders,
 * with an *Acknowledge* button. The acknowledgement is the whole point —
 * a notice nobody records acknowledging is a banner, and a banner is what
 * people have learnt to click past.
 *
 * **There is no draft.** A notice exists only once it is published: the
 * screen composes it and publishes it in one act. Two reasons. It is how a
 * safety notice actually works — you write it and put it up, you do not
 * leave it half-written in the system. And a draft would be a document
 * every member could read, because Firestore rules are all-or-nothing per
 * document and this collection has to be readable by the people it is for;
 * a half-written notice about an incident is not a thing to leave lying
 * where the person it concerns can read it.
 *
 * **Changing the wording bumps the revision, and that invalidates every
 * acknowledgement.** This is the decision the feature turns on. "Everybody
 * has acknowledged" has to mean "everybody has acknowledged *this text*",
 * so a correction asks for the acknowledgement again — including a typo
 * fix, which is a real cost and is said plainly on the screen rather than
 * discovered. The alternative, a silent edit, would leave a record saying
 * forty people agreed to wording they never saw, which is worse than
 * useless in the one conversation this record exists for.
 */

/** Who a notice is put in front of. */
export const NOTICE_AUDIENCES = ['internal', 'sub'] as const;
export const NoticeAudienceSchema = z.enum(NOTICE_AUDIENCES);
export type NoticeAudience = z.infer<typeof NoticeAudienceSchema>;

export const NOTICE_AUDIENCE_LABEL: Record<NoticeAudience, string> = {
  internal: 'Your own staff',
  sub: 'Subcontractors and vendors',
};

/**
 * Not `client`, and the omission is deliberate rather than pending.
 *
 * A homeowner is not working on the site and has no safety obligation to
 * the company; putting a hard-stop acknowledgement in front of a customer
 * reading their own invoices is a worse product and an odd thing to ask of
 * them. A sub **is** on site, which is why they are here.
 */
export const SafetyNoticeSchema = AuditSchema.extend({
  id: IdSchema,
  companyId: IdSchema,
  title: z.string().trim().min(1, 'A notice needs a title').max(200),
  body: z.string().trim().min(1, 'A notice with no wording asks nothing').max(5_000),
  /**
   * Bumped whenever the title or body changes. Acknowledgements carry it,
   * so one against an older revision does not count.
   */
  revision: z.number().int().min(1).default(1),
  /** Always present: see "there is no draft" above. */
  publishedAt: InstantSchema,
  /** Set when it is taken down. Nothing is deleted. */
  retiredAt: InstantSchema.optional(),
  audience: z.array(NoticeAudienceSchema).min(1).default(['internal']),
});
export type SafetyNotice = z.infer<typeof SafetyNoticeSchema>;

/**
 * One person's acknowledgement of one revision.
 *
 * **Keyed by uid and revision**, not by uid alone. Overwriting would lose
 * the fact that somebody acknowledged the previous wording on a particular
 * day, and that is exactly the fact this record exists to hold.
 *
 * `displayName` is copied rather than joined, like a notification's title:
 * a report of who acknowledged what has to keep saying the same thing
 * after somebody leaves and their member document goes.
 */
export const SafetyAcknowledgementSchema = z.object({
  id: IdSchema,
  companyId: IdSchema,
  noticeId: IdSchema,
  uid: IdSchema,
  revision: z.number().int().min(1),
  at: InstantSchema,
  displayName: z.string().max(200).optional(),
});
export type SafetyAcknowledgement = z.infer<typeof SafetyAcknowledgementSchema>;

/**
 * The document id for one person's acknowledgement of one revision.
 *
 * Derived rather than generated, which is what makes the callable
 * idempotent and the client's check a single `get` instead of a query: "has
 * this person acknowledged what is up now" is one document read at a known
 * path.
 */
export function acknowledgementId(uid: string, revision: number): string {
  return `${uid}-r${revision}`;
}

/** Live until it is retired. */
export function isNoticeLive(notice: Pick<SafetyNotice, 'retiredAt'>): boolean {
  return !notice.retiredAt;
}

/**
 * Whether this notice is for somebody of this kind.
 *
 * A `client` is never in the audience — see the note on the schema — so
 * this answers false for them whatever the stored list says, which matters
 * because the list is a stored field and a future writer could put one
 * there.
 */
export function noticeReaches(
  /**
   * Widened to `string[]`, not `NoticeAudience[]`, on purpose: this is
   * called with a list read straight out of Firestore — by the callable,
   * which has no schema in hand — and narrowing the parameter would move
   * the check to a cast at the one call site that most needs it.
   */
  notice: { readonly audience: readonly string[]; readonly retiredAt?: string },
  kind: string,
): boolean {
  if (!isNoticeLive(notice)) return false;
  if (kind !== 'internal' && kind !== 'sub') return false;
  return notice.audience.includes(kind);
}

/**
 * Whether the wording changed, and so whether the revision must move.
 *
 * Trimmed before comparing, because whitespace is not wording and asking
 * forty people to acknowledge a notice again over a trailing space would
 * teach them the button means nothing.
 */
export function wordingChanged(
  before: Pick<SafetyNotice, 'title' | 'body'>,
  after: Pick<SafetyNotice, 'title' | 'body'>,
): boolean {
  return before.title.trim() !== after.title.trim() || before.body.trim() !== after.body.trim();
}

/**
 * The notices this person still has to acknowledge.
 *
 * Takes the acknowledgements it was given rather than fetching: the caller
 * reads one document per live notice at a known path, and a function that
 * decided to query would make a hook of a calculation.
 */
export function unacknowledged(
  notices: readonly SafetyNotice[],
  kind: string,
  acknowledgedIds: ReadonlySet<string>,
  uid: string,
): SafetyNotice[] {
  return notices.filter(
    (notice) =>
      noticeReaches(notice, kind) && !acknowledgedIds.has(acknowledgementId(uid, notice.revision)),
  );
}

/**
 * How far through a company a notice has got.
 *
 * `acknowledged` counts only the **current** revision, which is the figure
 * somebody turning this on actually wants: "how many of my people have
 * read what is up now". Counting every revision would let a notice look
 * fully acknowledged the moment after it was reworded.
 */
export interface NoticeUptake {
  readonly acknowledged: number;
  readonly expected: number;
  readonly outstanding: readonly string[];
}

export function noticeUptake(
  notice: Pick<SafetyNotice, 'revision'>,
  expectedUids: readonly string[],
  acknowledgements: readonly Pick<SafetyAcknowledgement, 'uid' | 'revision'>[],
): NoticeUptake {
  const done = new Set(
    acknowledgements
      .filter((entry) => entry.revision === notice.revision)
      .map((entry) => entry.uid),
  );
  const outstanding = expectedUids.filter((uid) => !done.has(uid));
  return {
    // Counted from the expected list rather than from the acknowledgements,
    // so somebody who has since left does not inflate the figure.
    acknowledged: expectedUids.length - outstanding.length,
    expected: expectedUids.length,
    outstanding,
  };
}
